import { AfterViewChecked, Directive, ElementRef, forwardRef, HostBinding, inject, Input, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yRole } from '../interfaces/a11y.interfaces';
import { normalizeAriaValue } from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

const DEFAULT_ARIA_VALUE_MIN = 0;
const DEFAULT_ARIA_VALUE_MAX = 100;

@Directive({
  selector: '[khA11yProgressbar], progressbar',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yProgressbarDirective) }]
})
export class A11yProgressbarDirective extends A11yLabelDirective implements A11yRole, AfterViewChecked {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _translationService = inject(TranslationService);
  private _defaultLabel = 'Progress';

  /** Current value for hosts that do not generate their own progressbar markup. */
  @Input() public a11yValue: number | string | null = null;
  /** Minimum value for hosts that do not generate their own progressbar markup. */
  @Input() public a11yMin: number | string | null = null;
  /** Maximum value for hosts that do not generate their own progressbar markup. */
  @Input() public a11yMax: number | string | null = null;
  /** Explicit value announcement. When omitted, a localized value text is generated. */
  @Input() public a11yValueText: string | null = null;

  @HostBinding('attr.role') public get role(): string | null {
    return this.isBootstrapProgressbarHost() ? null : 'progressbar';
  }

  @HostBinding('attr.aria-valuenow') public get ariaValueNow(): string | null {
    return this.isBootstrapProgressbarHost() ? null : this.formatNumber(this.a11yValue);
  }

  @HostBinding('attr.aria-valuemin') public get ariaValueMin(): string | null {
    if (this.isBootstrapProgressbarHost() || this.ariaValueNow === null) {
      return null;
    }

    return this.formatNumber(this.a11yMin) ?? `${DEFAULT_ARIA_VALUE_MIN}`;
  }

  @HostBinding('attr.aria-valuemax') public get ariaValueMax(): string | null {
    if (this.isBootstrapProgressbarHost() || this.ariaValueNow === null) {
      return null;
    }

    return this.formatNumber(this.a11yMax) ?? `${DEFAULT_ARIA_VALUE_MAX}`;
  }

  @HostBinding('attr.aria-valuetext') public get ariaValueText(): string | null {
    if (this.isBootstrapProgressbarHost()) {
      return null;
    }

    return normalizeAriaValue(this.a11yValueText) ??
      this.buildValueText(this.ariaValueNow, this.ariaValueMin, this.ariaValueMax);
  }

  public constructor() {
    super();
    this._translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._defaultLabel = this._translationService.instant('A11Y_PROGRESS_BAR.PROGRESSBAR') || this._defaultLabel;
      });
  }

  protected override get ariaLabelValue(): string | null {
    return normalizeAriaValue(this.a11yLabel) ?? this._defaultLabel;
  }

  public ngAfterViewChecked(): void {
    this.syncInternalProgressbars();
  }

  private syncInternalProgressbars(): void {
    if (!this.isBootstrapProgressbarHost()) {
      return;
    }

    const internalProgressbars = this._elementRef.nativeElement.querySelectorAll<HTMLElement>('[role="progressbar"]');

    internalProgressbars.forEach((progressbar) => {
      this.syncAttribute(progressbar, 'aria-label', this.ariaLabelledBy ? null : this.ariaLabelValue);
      this.syncAttribute(progressbar, 'aria-labelledby', this.ariaLabelledBy);
      this.syncAttribute(progressbar, 'aria-describedby', this.ariaDescribedBy);
      this.syncValueAttributes(progressbar);
    });
  }

  private syncValueAttributes(progressbar: HTMLElement): void {
    const valueNow = normalizeAriaValue(progressbar.getAttribute('aria-valuenow')) ?? `${DEFAULT_ARIA_VALUE_MIN}`;
    const valueMin = normalizeAriaValue(progressbar.getAttribute('aria-valuemin')) ?? `${DEFAULT_ARIA_VALUE_MIN}`;
    const valueMax = normalizeAriaValue(progressbar.getAttribute('aria-valuemax')) ?? `${DEFAULT_ARIA_VALUE_MAX}`;

    this.syncAttribute(progressbar, 'aria-valuenow', valueNow);
    this.syncAttribute(progressbar, 'aria-valuemin', valueMin);
    this.syncAttribute(progressbar, 'aria-valuemax', valueMax);
    this.syncAttribute(
      progressbar,
      'aria-valuetext',
      normalizeAriaValue(this.a11yValueText) ?? this.buildValueText(valueNow, valueMin, valueMax)
    );
  }

  private buildValueText(valueNow: string | null, valueMin: string | null, valueMax: string | null): string | null {
    if (valueNow === null) {
      return null;
    }

    const parsedMin = valueMin === null ? DEFAULT_ARIA_VALUE_MIN : Number(valueMin);
    const parsedMax = valueMax === null ? DEFAULT_ARIA_VALUE_MAX : Number(valueMax);
    const min = Number.isFinite(parsedMin) ? parsedMin : DEFAULT_ARIA_VALUE_MIN;
    const max = Number.isFinite(parsedMax) ? parsedMax : DEFAULT_ARIA_VALUE_MAX;
    if (min === DEFAULT_ARIA_VALUE_MIN && max === DEFAULT_ARIA_VALUE_MAX) {
      return normalizeAriaValue(this._translationService.instant('A11Y_PROGRESS_BAR.PERCENT', { value: valueNow }));
    }

    return normalizeAriaValue(
      this._translationService.instant('A11Y_PROGRESS_BAR.VALUE_OF_MAX', { value: valueNow, max: `${max}` })
    );
  }

  private formatNumber(value: number | string | null): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? `${Math.round(numericValue * 100) / 100}` : null;
  }

  private syncAttribute(element: HTMLElement, attributeName: string, value: string | null): void {
    if (value) {
      this._renderer.setAttribute(element, attributeName, value);
      return;
    }

    this._renderer.removeAttribute(element, attributeName);
  }

  private isBootstrapProgressbarHost(): boolean {
    return this._elementRef.nativeElement.tagName.toLowerCase() === 'progressbar';
  }
}
